/**
 * Glue บาง ๆ ฝั่งเบราว์เซอร์ของปุ่ม "Download All Audio / Download All Video"
 * (fetch ไฟล์รายฉาก → ยัดลง zip ทีละไฟล์ → คืน Blob ให้ `saveAs`)
 *
 * ทำไมไฟล์นี้ "ไม่มีเทสต์": ทุกบรรทัดพึ่ง API ของเบราว์เซอร์ล้วน ๆ (fetch, Blob, stream ของ zip.js)
 * ซึ่ง jest ของโปรเจกต์นี้รันไม่ได้ — environment node ไม่มี และ jsdom ก็ใช้ไม่ได้เพราะ native module
 * ของ canvas ในเครื่อง/CI ชุดนี้พัง ⇒ ตรรกะที่ "ผิดแล้วได้ zip ผิด" (ลำดับฉาก ชื่อ entry เงื่อนไขปุ่ม
 * ข้อความ error) จึงอยู่ใน `episodeDownload.ts` ซึ่งเป็น pure และมีเทสต์ครบ เหลือในไฟล์นี้เฉพาะการต่อสาย
 * ที่ต้องทดสอบมือในเบราว์เซอร์จริงอยู่ดี
 *
 * ใช้ `@zip.js/zip.js` ตัวเดียวกับที่ export ของ CMS ใช้อยู่แล้ว (`apps/CMS/utils/exportUtils.ts`)
 * จึงไม่ต้องเพิ่ม dependency ใหม่ · ตั้ง `level: 0` (store อย่างเดียว ไม่บีบอัด) เพราะ mp4/mp3/wav
 * ถูกบีบมาแล้ว บีบซ้ำแทบไม่ลดขนาดแต่กิน CPU เต็ม ๆ ต่อไฟล์ · ลำดับ entry ในไฟล์ zip ตรงกับลำดับฉาก
 * บนจอเพราะเรา `await` การ `add()` ทีละไฟล์เรียงกันอยู่แล้ว (ไม่ใช่เพราะ option ของ zip.js) · ส่ง
 * `signal` ให้ zip.js ด้วย เพื่อให้ยกเลิกไฟล์ที่กำลังเขียนค้างอยู่ได้ทันที (AbortError ที่โยนออกมา
 * `isAbortError` รับมือให้แล้ว)
 *
 * ทำไม `<a download>` แทนไม่ได้: ไฟล์อยู่คนละ origin กับหน้าเว็บ (host ของ MeGen API) เบราว์เซอร์จะ
 * ไม่ยอมตั้งชื่อไฟล์/ไม่บังคับดาวน์โหลดให้ กลายเป็นเปิดแท็บใหม่แทน ⇒ ต้อง fetch เนื้อไฟล์เข้ามาเอง
 * และ origin ของหน้าต้องอยู่ใน allowlist CORS ของ API ด้วย
 */
import { BlobReader, BlobWriter, ZipWriter } from "@zip.js/zip.js";

import { EpisodeDownloadError, isAbortError, ZipEntry } from "./episodeDownload";

/**
 * โหลดไฟล์สื่อหนึ่งไฟล์เป็น Blob — ใช้ทั้งกับไฟล์ final (โหลดตรง) และไฟล์รายฉากที่จะเอาไปใส่ zip
 *
 * `fetch` reject เป็น `TypeError` ล้วน ๆ ทั้งตอนเน็ตหลุดและตอน CORS ไม่ผ่าน (หลังบ้าน allowlist origin
 * แบบ hardcode) ⇒ แยกสาเหตุจากตัว error ไม่ได้ ต้องรวมเป็น `fetch_failed` แล้วให้ข้อความบอก host/origin
 * ส่วน 404/5xx ต้องเช็ค `res.ok` เอง เพราะ fetch ถือว่า "ตอบแล้ว" = สำเร็จ (ของเดิมเซฟ HTML ของหน้า 404
 * เป็นไฟล์ .mp4) และ AbortError ต้องโยนต่อดิบ ๆ เพื่อให้ชั้นบนแยก "ผู้ใช้สลับ run" ออกจากความผิดพลาดจริง
 *
 * `cache: "no-cache"` (บังคับ revalidate ทุกครั้ง): ชื่อไฟล์รายฉาก/เสียงเป็นชื่อตายตัว
 * (`segment_{id}_final.mp4` ถูกเขียนทับเมื่อ regenerate) และ FileResponse ของหลังบ้านไม่ส่ง
 * `Cache-Control` มาด้วย ⇒ fetch โหมดปกติมีสิทธิ์คืนไฟล์เก่าจาก cache ของเบราว์เซอร์ ทั้งที่ player
 * บนจอเลี่ยงปัญหานี้ด้วย `?v=` ไปแล้ว (ตัวดาวน์โหลดใช้ URL ดิบจาก `/output` จึงไม่มี `?v=` กันให้)
 */
export const fetchMediaBlob = async (url: string, signal: AbortSignal): Promise<Blob> => {
  try {
    const res = await fetch(url, { signal, cache: "no-cache" });
    if (!res.ok) throw new EpisodeDownloadError("http_error", String(res.status));
    return await res.blob();
  } catch (err) {
    if (err instanceof EpisodeDownloadError || isAbortError(err)) throw err;
    throw new EpisodeDownloadError("fetch_failed", err instanceof Error ? err.message : undefined);
  }
};

/** ความคืบหน้าของการรวม zip ใช้โชว์ "กำลังรวมไฟล์ 3/12" ระหว่างดาวน์โหลด */
export type ZipProgress = { done: number; total: number };

/**
 * โหลดไฟล์ตาม `entries` แล้วรวมเป็น zip ไฟล์เดียว ตามลำดับที่ส่งเข้ามา (คำนวณมาจาก `zipEntriesFor`)
 *
 * โหลด/เขียนทีละไฟล์เรียงกันโดยตั้งใจ ไม่ทำขนาน: ตอนหนึ่งมีได้หลายสิบฉาก ถ้าดึงพร้อมกันทั้งตอนจะถือ Blob
 * ของทุกฉากไว้ในแรมพร้อมกัน (วิดีโอฉากละหลาย MB) แถมรายงานความคืบหน้าเป็นลำดับไม่ได้
 * ยังเช็ค `signal.aborted` คั่นทุกไฟล์อยู่ แม้จะส่ง signal ให้ ZipWriter แล้ว เพราะ abort ที่เกิดตอน
 * "ระหว่างไฟล์" (ไม่มี add/fetch ค้างอยู่) ไม่มีใครโยน error ให้ ⇒ ต้องหยุดเองก่อนเสียเวลากับไฟล์ถัดไป
 */
export const buildZipFromUrls = async (
  entries: ZipEntry[],
  opts: { signal: AbortSignal; onProgress?: (p: ZipProgress) => void },
): Promise<Blob> => {
  if (entries.length === 0) throw new EpisodeDownloadError("no_sources");

  const writer = new ZipWriter(new BlobWriter("application/zip"), {
    level: 0,
    signal: opts.signal,
  });

  for (let i = 0; i < entries.length; i += 1) {
    if (opts.signal.aborted) throw new EpisodeDownloadError("aborted");

    let blob: Blob;
    try {
      blob = await fetchMediaBlob(entries[i].url, opts.signal);
    } catch (err) {
      // zip มีหลายสิบไฟล์ ⇒ "เซิร์ฟเวอร์ตอบ 404" เฉย ๆ ไม่พอ ต้องบอกด้วยว่าฉากไหนพัง ผู้ใช้จะได้ไปเรนเดอร์ซ้ำถูกฉาก
      if (err instanceof EpisodeDownloadError && err.code === "http_error") {
        throw new EpisodeDownloadError("http_error", `${err.message} (${entries[i].name})`);
      }
      throw err;
    }
    // ไฟล์ 0 ไบต์เกิดได้จาก S3 lazy-download ที่ค้างอยู่ ⇒ ต้องไม่กลายเป็น entry ว่างใน zip เงียบ ๆ
    // (ผู้ใช้จะรู้ตัวก็ต่อเมื่อแตกไฟล์แล้วเปิดคลิปนั้นไม่ได้)
    if (blob.size === 0) {
      throw new EpisodeDownloadError("empty_file", entries[i].name);
    }
    try {
      await writer.add(entries[i].name, new BlobReader(blob));
    } catch (err) {
      if (err instanceof EpisodeDownloadError || isAbortError(err)) throw err;
      // zip.js โยน error ของตัวเอง (เช่นชื่อ entry ซ้ำ / เขียนลง stream ไม่ได้) ⇒ แปลงเป็น code ของเราให้ UI อ่านออก
      throw new EpisodeDownloadError("zip_failed", err instanceof Error ? err.message : undefined);
    }
    opts.onProgress?.({ done: i + 1, total: entries.length });
  }

  if (opts.signal.aborted) throw new EpisodeDownloadError("aborted");

  try {
    // close() คือขั้นที่เขียน central directory และคืน Blob ของไฟล์ zip ทั้งก้อน
    return await writer.close();
  } catch (err) {
    if (err instanceof EpisodeDownloadError || isAbortError(err)) throw err;
    throw new EpisodeDownloadError("zip_failed", err instanceof Error ? err.message : undefined);
  }
};
